package com.evoapps.evoshift.domain.model

import kotlinx.serialization.Serializable

/**
 * SH-196: the colours a person picked for the app: the [base] (the bars, the rail, the dark cards
 * and dialogs) and the [accent] (the highlights on them). Navy with gold is the app's own look
 * and the default. Kept on the person's profile (users/{uid}.appearance) and on each device.
 */
@Serializable
data class Appearance(val base: ThemeBase = ThemeBase.NAVY, val accent: ThemeAccent = ThemeAccent.GOLD) {
    companion object {
        val DEFAULT = Appearance()

        /**
         * From the stored names ("BURGUNDY", "SILVER"): anything missing or unknown (a newer
         * app's colour, a typo) is the default's, each on its own.
         */
        fun parse(base: String?, accent: String?): Appearance = Appearance(
            base = ThemeBase.entries.firstOrNull { it.name == base } ?: DEFAULT.base,
            accent = ThemeAccent.entries.firstOrNull { it.name == accent } ?: DEFAULT.accent
        )

        /** The device's cache: "BASE/ACCENT" (see [stored]). */
        fun fromStored(value: String?): Appearance =
            value?.split('/')?.takeIf { it.size == 2 }?.let { parse(it[0], it[1]) } ?: DEFAULT
    }

    /** "BURGUNDY/SILVER", for the device's cache. */
    val stored: String get() = "${base.name}/${accent.name}"
}

/** SH-196: the base colour: navy (granat), bottle green or burgundy (bordo). */
enum class ThemeBase { NAVY, BOTTLE_GREEN, BURGUNDY }

/** SH-196: the accent colour: gold, bronze or silver. */
enum class ThemeAccent { GOLD, BRONZE, SILVER }
